<template>
  <div :id="containerId" class="unlayer-editor" style="width:100%;height:86vh;"></div>
</template>

<script>
import __tags from '@/assets/plugins/email-editor/merge-tags.js';
import { get_domain_url, get_base_url, downloadWithAxios } from '@/core/services/fileDownload';


export default {
  name: 'UnlayerWrapperComponent',
  emits: ['plaintextGenerated','auto_save'],
  props: {

    userId: {
      type: [String],
      default: 'test'
    },
    /**
     * Your Unlayer project ID (optional if you only use the free tier)
     */
    projectId: {
      type: [String, Number],
      default: null
    },

    /**
     * Initial design JSON to load into the editor
     */
    design: {
      type: Object,
      default: null
    },

    /**
     * Whether to put the editor in read-only/preview mode
     */
    readOnly: {
      type: Boolean,
      default: false
    },

    type: {
      type: String,
      default: 'email'
    },

    /**
     * Appearance configuration
     */
    appearance: {
      type: Object,
      default: null
    },

    /**
     * Locale to use inside the editor (e.g. 'en-US', 'sv-SE')
     */
    locale: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      saveTimer: null,
      SAVE_DELAY: 1000,
      options: {},
      containerId: `unlayer-editor-${this._uid}`,
      editorReady: false
    }
  },

  mounted () {
    

    this.options.mergeTags = {};
    
    __tags.filter(item => {
      
      this.options.mergeTags[item.id] = { name: item.name, value: `%${item.id}%` };
    });
  
    this._ensureScriptLoaded()
      .then(this._initEditor)
      .catch(err => {
        // Surface the error so consumers can react
        this.$emit('error', err)
        // eslint-disable-next-line no-console
        console.error('[UnlayerWrapperComponent] Failed to load Unlayer script', err)
      })
  },

  beforeDestroy () {
    // Safely destroy the editor instance when navigating away
    if (window.unlayer && this.editorReady) {
      try {
        window.unlayer.destroy(this.containerId)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[UnlayerWrapperComponent] Failed to destroy editor', e)
      }
    }
  },

  methods: {

    exportPlainText() {
      return new Promise(async (resolve, reject) => {
        var that = this;

        window.unlayer.exportPlainText(function (data) {
          var text = data.text;

          resolve(text);
        });
      });

    },
    exportHTML() {
      return new Promise(async (resolve, reject) => {

        window.unlayer.saveDesign(
          (data) => {
            window.unlayer.exportHtml(
              (val) => {
                resolve({
                  edit_html: data,
                  html: val.html
                });
              }
            )
          }
        );
      });

    },
    saveDesign() {
      var that = this;

      window.unlayer.exportPlainText(function (data) {
        var text = data.text;

        that.$emit('plaintextGenerated', text);
      });

      window.unlayer.saveDesign(
        (data) => {
          window.unlayer.exportHtml(
            (val) => {
              resolve({
                edit_html: data,
                html: val.html
              });
            })
        }
      );
    },


    /**
     * Load a new design JSON into the editor
     */
    loadDesign (designJson) {
      if (this.editorReady && window.unlayer && designJson) {
        window.unlayer.loadDesign(designJson)
      }
    },

    /**
     * Ensure the embed.js script is present exactly once.
     * Returns a Promise that resolves when window.unlayer exists.
     */
    _ensureScriptLoaded () {
      return new Promise((resolve, reject) => {
        if (typeof window !== 'undefined' && window.unlayer) {
          return resolve()
        }

        // If another instance is already loading the script, wait for it
        if (document.getElementById('unlayer-embed-script')) {
          const checkInterval = setInterval(() => {
            if (window.unlayer) {
              clearInterval(checkInterval)
              resolve()
            }
          }, 50)
          return
        }

        const script = document.createElement('script')
        script.id = 'unlayer-embed-script'
        script.type = 'text/javascript'
        script.src = 'https://editor.unlayer.com/embed.js'
        script.async = true
        script.onload = () => resolve()
        script.onerror = reject
        document.head.appendChild(script)
      })
    },

    performSave() {
      window.unlayer.exportPlainText((data) => {
        var text = data.text;

        this.$emit('plaintextGenerated', text);
      });

      window.unlayer.saveDesign((design) => {
        window.unlayer.exportHtml(({ html }) => {
          this.$emit('auto_save', { edit_html: design, html });
        });
      });
    },

    /**
     * Initialize the unlayer editor once the script is loaded
     */
    _initEditor () {
      if (!window.unlayer) {
        throw new Error('Unlayer script missing even after load')
      }


      const initConfig = {
        version: 'stable',
        id: this.containerId,
        projectId: 14545,
        displayMode: 'email',
        mergeTags: this.options.mergeTags,
        tools: {
          video: {
            enabled: true,
          },
        },

        user: {
          id: this.userId,
        },
        
        features: {
          userUploads: true,
          textEditor: {
            spellChecker: true,
          },
        },
        appearance: this.appearance || undefined,
        locale: this.locale || undefined,
        
        customJS: this.type === 'survey' ? [
          'http://localhost:8080/js/survey-radio-unlayer.js',
        ] : [ 'http://localhost:8080/js/rss-unlayer.js' ],
      }

      window.unlayer.init(initConfig);

      // Forward useful events to Vue consumers
      window.unlayer.addEventListener('editor:ready', () => {
        this.editorReady = true
        this.$emit('ready', window.unlayer)

        // Load initial design if provided
        if (this.design) {
          window.unlayer.loadDesign(this.design)
        }
      });

      const scheduleSave = () => {
        clearTimeout(this.saveTimer); 
        this.saveTimer = setTimeout(this.performSave, this.SAVE_DELAY);
      };
      
      window.unlayer.addEventListener('design:updated', scheduleSave);

      this.$once('hook:beforeDestroy', () => {
        window.unlayer.removeEventListener('design:updated', scheduleSave);
        clearTimeout(this.saveTimer);
      });

      window.unlayer.addEventListener('design:loaded', data => {
        this.$emit('design-loaded', data)
      })
    }
  },

  watch: {
    design (newDesign) {
      if (newDesign) {
        this.loadDesign(newDesign)
      }
    }
  }
}
</script>

<style scoped>
.unlayer-editor {
  /* Make sure the editor expands to fill its parent */
  width: 100%;
  height: 100%;
}
</style>
